// src/screens/WealthMultiplier.tsx
// ─────────────────────────────────────────────────────────────────────────────
// Brief 42 Section 3 + Brief 46A Section 8 — Wealth Multiplier
// Bridge screen between The Gap and Order of Operations.
// Shows compounding power and the monthly number that closes the gap.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useMemo } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  ReferenceLine, ResponsiveContainer, Legend, Tooltip,
} from 'recharts';
import { ClientData } from '../types/ClientData';
import { formatCurrency, formatCurrencyK } from '../utils/format';

interface Props {
  data: ClientData;
  onUpdate: (updates: Partial<ClientData>) => void;
}

const fmt = formatCurrency;
const fmtK = formatCurrencyK;

const GROWTH_RATE = 0.08;
const MONTHLY_RATE = GROWTH_RATE / 12;

export default function WealthMultiplier({ data }: Props) {
  const age = data.clientAge || 40;
  const freedomAge = data.targetFreedomAge || 60;
  const years = Math.max(0, freedomAge - age);
  const target = data.freedomNumberInflated || 0;

  const currentInvested = (data.pensionBalance || 0) + (data.investmentsBalance || 0);

  const wealthMultiplier = Math.pow(1 + GROWTH_RATE, years);

  const fvCurrentPot = target > 0
    ? currentInvested * Math.pow(1 + GROWTH_RATE, years)
    : 0;

  const n = years * 12;
  const annuityFactor = MONTHLY_RATE > 0
    ? (Math.pow(1 + MONTHLY_RATE, n) - 1) / MONTHLY_RATE
    : n;

  const monthlyNeeded = Math.max(0, (target - fvCurrentPot) / annuityFactor);

  const onTrack = monthlyNeeded <= 0 && target > 0;

  // Chart data: annual steps from current age to freedom age
  const chartData = useMemo(() => {
    if (years <= 0 || target <= 0) return [];
    const points: Array<{ age: number; currentPath: number; withInvesting: number }> = [];
    for (let t = 0; t <= years; t++) {
      const currentPath = currentInvested * Math.pow(1 + GROWTH_RATE, t);
      const withInvesting = currentPath +
        (MONTHLY_RATE > 0
          ? monthlyNeeded * ((Math.pow(1 + MONTHLY_RATE, t * 12) - 1) / MONTHLY_RATE)
          : monthlyNeeded * t * 12);
      points.push({
        age: age + t,
        currentPath: Math.round(currentPath),
        withInvesting: Math.round(withInvesting),
      });
    }
    return points;
  }, [age, years, currentInvested, monthlyNeeded, target]);

  // ── Edge case: no target set ─────────────────────────────────────────────
  if (!target || target <= 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center text-gray-400 max-w-md">
          <div className="text-5xl mb-4">🔢</div>
          <h2 className="text-xl font-bold text-gray-500 mb-2">Complete setup to see your wealth multiplier</h2>
          <p className="text-sm">Your Freedom Number needs to be calculated first.</p>
        </div>
      </div>
    );
  }

  // ── Edge case: freedom age passed ────────────────────────────────────────
  if (years <= 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center text-gray-400 max-w-md">
          <div className="text-5xl mb-4">⏰</div>
          <h2 className="text-xl font-bold text-gray-500 mb-2">Freedom age has passed</h2>
          <p className="text-sm">Your target freedom age is at or before your current age.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      {/* ── LEFT PANEL ─────────────────────────────────────────────────────── */}
      <div className="w-2/5 bg-ma-navy text-white p-10 flex flex-col justify-center">
        <div className="mb-8">
          <div className="text-xs uppercase tracking-widest text-white/50 mb-2">Your Wealth Multiplier</div>
          <div className="text-6xl font-black" style={{ color: '#C9A84C' }}>
            {wealthMultiplier.toFixed(2)}×
          </div>
          <p className="text-sm text-white/60 mt-2">
            Every £1 invested today becomes £{wealthMultiplier.toFixed(2)} by age {freedomAge}
          </p>
        </div>

        <div className="border-t border-white/20 my-6" />

        <div className="mb-6">
          <div className="text-xs uppercase tracking-widest text-white/50 mb-1">To close the gap</div>
          <div className="text-3xl font-bold" style={{ color: '#C9A84C' }}>{fmt(Math.round(target))}</div>
        </div>

        <div className="border-t border-white/20 my-6" />

        {onTrack ? (
          <div className="mb-6">
            <div className="text-xs uppercase tracking-widest text-white/50 mb-1">Status</div>
            <div className="text-3xl font-bold text-green-400">You're already on track</div>
            <p className="text-sm text-white/50 mt-1">Your current pot projects above your Freedom Number.</p>
          </div>
        ) : (
          <div className="mb-6">
            <div className="text-xs uppercase tracking-widest text-white/50 mb-1">You need to invest</div>
            <div className="text-3xl font-bold" style={{ color: '#64DAD2' }}>{fmt(Math.round(monthlyNeeded))}/month</div>
            <p className="text-sm text-white/50 mt-1">to close the gap by age {freedomAge}</p>
          </div>
        )}

        <div className="mt-auto pt-8">
          <p className="text-xs text-white/30">
            Assumes {Math.round((data.assumptionPensionGrowth ?? 0.08) * 100)}% annual growth, compounded monthly. Planning purposes only.
          </p>
        </div>
      </div>

      {/* ── RIGHT PANEL ────────────────────────────────────────────────────── */}
      <div className="w-3/5 bg-white p-10 flex flex-col justify-center">
        <h3 className="text-xl font-bold text-ma-navy mb-6">Your wealth trajectory</h3>

        <div style={{ width: '100%', height: 420 }}>
          <ResponsiveContainer>
            <LineChart data={chartData} margin={{ top: 20, right: 30, bottom: 20, left: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="age"
                tick={{ fill: '#6b7280', fontSize: 11 }}
                label={{ value: 'Age', position: 'insideBottom', offset: -10, fill: '#9ca3af', fontSize: 11 }}
              />
              <YAxis
                tickFormatter={(v: number) => {
                  if (v >= 1000000) return `£${(v / 1000000).toFixed(1)}m`;
                  return fmtK(v);
                }}
                tick={{ fill: '#6b7280', fontSize: 11 }}
              />
              <Tooltip formatter={(v: any) => fmt(Number(v))} />
              <Line
                type="monotone"
                dataKey="currentPath"
                stroke="#1E3A5F"
                strokeWidth={2}
                dot={false}
                name="Current path"
              />
              {!onTrack && (
                <Line
                  type="monotone"
                  dataKey="withInvesting"
                  stroke="#64DAD2"
                  strokeWidth={3}
                  dot={false}
                  name="With monthly investing"
                />
              )}
              <ReferenceLine
                y={target}
                stroke="#EF4444"
                strokeDasharray="8 4"
                strokeWidth={2}
                label={{
                  value: `Freedom Number ${fmtK(target)}`,
                  position: 'insideTopRight',
                  fill: '#EF4444',
                  fontSize: 11,
                  fontWeight: 'bold',
                }}
              />
              <Legend
                verticalAlign="top"
                wrapperStyle={{ paddingBottom: 10 }}
                iconType="line"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
